// src/products/dataroom/components/DataroomExplorer.js
// Complete DataroomExplorer component for file and folder management
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { 
  Folder, MessageSquare, Bookmark, AlertCircle, X
} from 'lucide-react';

// FIX: Import supabase client
import { supabase } from '../../../config'; 

// Import extracted components
import DataroomItemRenderer from './DataroomItemRenderer';
import DataroomToolbar from './DataroomToolbar';

// Modals (ONLY importing the ones used locally for localModals state/JSX)
import UploadFileModal from './Modals/UploadFileModal';
import CreateFolderModal from './Modals/CreateFolderModal';
import DocumentViewer from './Modals/DocumentViewer';
import PermissionManager from './Modals/PermissionManager';
import EmbedDataroomModal from './Modals/EmbedDataroomModal';
import MergeDataroomModal from './Modals/MergeDataroomModal';
import DeleteConfirmationModal from './Modals/DeleteConfirmationModal';
import ProjectColorModal from './Modals/ProjectColorModal';
import BookmarkModal from './Modals/BookmarkModal';

// FIX: Import the enhanced dataroom service
import dataroomService from '../../../services/dataroomService';
import { generateIndexMap, generateDownloadableIndex } from '../utils/indexing';

// Utility functions moved outside component for performance
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const buildTreeStructure = (items, parentId = null) => {
  if (!items || !Array.isArray(items)) return [];
  let index = 0;
  return items
    .filter(item => item.parent_id === parentId)
    .map(item => ({
      ...item,
      index: index++,
      children: item.is_folder ? buildTreeStructure(items, item.id) : []
    }));
};

const buildBreadcrumbPath = (targetFolder, itemsList) => {
  if (!targetFolder || !itemsList || !Array.isArray(itemsList)) return [];
  const path = [];
  let currentItem = targetFolder;
  
  while (currentItem) {
    path.unshift({ id: currentItem.id, name: currentItem.name });
    const parentId = currentItem.parent_id;
    currentItem = itemsList.find(item => item.id === parentId);
  }
  return path;
};

const DataroomExplorer = ({ 
  projectId, 
  user, 
  userRole, 
  onMessage, 
  onCreateQuestion, 
  projectName,
  projects = [],
  selectedProjectId,
  onProjectSelect,
  openModal, 
  modals,     
  closeModal, 
  onProjectColorChanged, 
  // NEW: Props for state lifted to DataroomApp
  searchTerm,
  breadcrumb = [],
  onSetBreadcrumb,
  onSetSearchTerm
}) => {
  // Core state
  const [items, setItems] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [indexMap, setIndexMap] = useState(new Map());

  // Local Modal state (for modals controlled entirely within Explorer: viewer, folder, permissions, etc.)
  const [localModals, setLocalModals] = useState({
    upload: false,
    createFolder: false,
    documentViewer: null,
    permissionManager: null,
    projectColor: null, 
    bookmark: null 
  });

  // Interaction states
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState(''); 

  // Local Modal helpers (for modals NOT shared with DataroomApp)
  const openLocalModal = useCallback((modalName, data = null) => {
    setLocalModals(prev => ({ 
      ...prev, 
      [modalName]: data !== null ? data : true 
    }));
  }, []);

  const closeLocalModal = useCallback((modalName) => {
    setLocalModals(prev => ({ 
      ...prev, 
      [modalName]: null 
    }));
  }, []);
  
  const handleBookmarkAdded = useCallback(() => {
    closeLocalModal('bookmark');
  }, [closeLocalModal]);
  
  const onSetEditName = useCallback((name) => {
    setEditName(name);
  }, []);

  const handleOpenModal = (modalName, data = null) => {
    const localModalsList = ['upload', 'createFolder', 'documentViewer', 'permissionManager', 'projectColor', 'bookmark'];
    if (localModalsList.includes(modalName)) {
      openLocalModal(modalName, data);
    } else {
      openModal(modalName, data);
    }
  };

  // Navigation functions
  const navigateToFolder = useCallback((folderId, folderName = null) => {
    if (folderId === currentFolder) return;
    
    setCurrentFolder(folderId);
    setSelectedFolderId(folderId);
    
    if (folderId !== null) {
      setExpandedFolders(prev => new Set(prev).add(folderId));
      
      // Log folder access
      if (user?.id && projectId) {
        supabase
          .from('dataroom_logs')
          .insert({
            user_id: user.id,
            project_id: projectId,
            action: 'folder_accessed',
            details: {
              folder_id: folderId,
              folder_name: folderName || 'Unknown Folder',
              access_type: 'navigation'
            }
          })
          .then(({ error }) => {
            if (error) console.warn('Failed to log folder access:', error);
          });
      }
    }
    // Breadcrumb update is handled by useEffect
  }, [currentFolder, user?.id, projectId]);

  // Load items from database with loading guard
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('dataroom_items')
        .select('*')
        .eq('project_id', projectId)
        .order('is_folder', { ascending: false })
        .order('name');

      if (error) throw error;

      setItems(data || []);
      setTreeData(buildTreeStructure(data || []));
    } catch (error) {
      console.error('Error loading items:', error);
      onMessage && onMessage('Failed to load items: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, onMessage]);

  // Initial load - Only run when projectId changes
  useEffect(() => {
    if (projectId) {
      loadItems();
      // Initialize breadcrumb on first load
      onSetBreadcrumb([{ id: null, name: projectName || 'Dataroom Home' }]);
      
      // Log project access
      if (user?.id) {
        supabase
          .from('dataroom_logs')
          .insert({
            user_id: user.id,
            project_id: projectId,
            action: 'project_accessed',
            details: {
              project_name: projectName || 'Unknown Project',
              access_type: 'explorer_view'
            }
          })
          .then(({ error }) => {
            if (error) console.warn('Failed to log project access:', error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Update tree data and index map when items change
  useEffect(() => {
    if (items && items.length > 0) {
      setTreeData(buildTreeStructure(items));
      setIndexMap(generateIndexMap(items));
    }
  }, [items]);

  // Update breadcrumb when currentFolder changes
  useEffect(() => {
    if (items && items.length > 0) {
      const folder = items.find(item => item.id === currentFolder);
      if (folder) {
        const path = buildBreadcrumbPath(folder, items);
        onSetBreadcrumb([{ id: null, name: projectName || 'Dataroom Home' }, ...path]);
      } else if (currentFolder === null) {
        onSetBreadcrumb([{ id: null, name: projectName || 'Dataroom Home' }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolder, items]);

  // Filter items based on search and current folder - Memoized for performance
  const filteredItems = useMemo(() => {
    return (items || [])
      .filter(item => {
        const matchesSearch = !searchTerm || 
          item.name.toLowerCase().includes(searchTerm.toLowerCase());
        const inCurrentFolder = item.parent_id === currentFolder;
        return matchesSearch && inCurrentFolder;
      })
      .map((item, index) => ({ ...item, index }));
  }, [items, searchTerm, currentFolder]);

  // File drag and drop handlers for the folder view (for external files only)
  const handleFileDrag = useCallback((e) => {
    // Only handle if dragging files from outside (not items from the list)
    // Check if it's a file drag (has Files in types) and not from react-beautiful-dnd
    if (e.dataTransfer.types.includes('Files') && !e.dataTransfer.types.includes('application/react-beautiful-dnd')) {
      e.preventDefault();
      e.stopPropagation();
      if (e.type === 'dragenter' || e.type === 'dragover') {
        setDragActive(true);
        e.dataTransfer.dropEffect = 'copy';
      } else if (e.type === 'dragleave') {
        // Only deactivate if we're actually leaving the drop zone
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
          setDragActive(false);
        }
      }
    }
  }, []);

  const handleFileDrop = useCallback(async (e) => {
    // Only handle file drops (not item reordering)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      // Make sure this isn't an item being dragged
      if (e.dataTransfer.types.includes('application/react-beautiful-dnd')) {
        return; // Let react-beautiful-dnd handle it
      }

      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      
      // Import services for file upload and validation
      const itemService = (await import('../services/itemService')).default;
      const { validateFileSecurity } = await import('../utils/fileSecurityRules');
      
      let successful = 0;
      let failed = 0;

      for (const file of files) {
        try {
          // Validate file security before upload
          const validation = validateFileSecurity(file);
          if (!validation.isValid) {
            failed++;
            const errorMsg = validation.errors[0] || 'File validation failed';
            console.error(`❌ File validation failed for ${file.name}:`, errorMsg);
            onMessage && onMessage(`${file.name}: ${errorMsg}`, 'error');
            
            // Show red popup for blocked files
            if (errorMsg.includes('FILE NOT ALLOWED')) {
              setErrorPopupMessage(errorMsg);
              setShowErrorPopup(true);
              // Auto-hide after 8 seconds
              setTimeout(() => setShowErrorPopup(false), 8000);
            }
            continue; // Skip this file
          }
          
          // Log warnings if any
          if (validation.warnings.length > 0) {
            console.warn(`⚠️ File validation warnings for ${file.name}:`, validation.warnings);
          }
          
          console.log(`Starting upload for file: ${file.name} (${(file.size / 1024).toFixed(2)} KB)`);
          const result = await itemService.uploadFile(
            parseInt(projectId),
            currentFolder ? parseInt(currentFolder) : null,
            file
          );

          if (result.error) {
            failed++;
            console.error(`❌ Failed to upload ${file.name}:`, result.error);
            onMessage && onMessage(`Failed to upload ${file.name}: ${result.error.message || result.error}`, 'error');
          } else {
            successful++;
            console.log(`✅ Successfully uploaded ${file.name}`);
          }
        } catch (error) {
          failed++;
          console.error(`❌ Exception uploading ${file.name}:`, error);
          const errorMsg = error.message || String(error);
          onMessage && onMessage(`Failed to upload ${file.name}: ${errorMsg}`, 'error');
        }
      }

      if (successful > 0) {
        onMessage && onMessage(`Uploaded ${successful} file(s) successfully`, 'success');
      }
      if (failed > 0) {
        onMessage && onMessage(`Failed to upload ${failed} file(s)`, 'error');
      }

      // Reload items to show newly uploaded files
      await loadItems();
    }
  }, [projectId, currentFolder, onMessage, loadItems]);

  // Event handlers for child components
  const handleFileUploadComplete = useCallback(async (result) => {
    onMessage && onMessage(`Uploaded ${result.successful} files successfully`, 'success');
    if (result.failed > 0) {
      onMessage && onMessage(`Failed to upload ${result.failed} files`, 'error');
    }
    closeLocalModal('upload');
    await loadItems();
  }, [onMessage, closeLocalModal, loadItems]);

  const handleFolderCreated = useCallback(async (folderName) => {
    try {
      // Use ItemService which handles permissions and subscription checks
      const itemService = (await import('../services/itemService')).default;
      const result = await itemService.createFolder(
        parseInt(projectId),
        currentFolder ? parseInt(currentFolder) : null,
        { name: folderName }
      );

      if (result.error) {
        // Provide more helpful error messages
        const errorMsg = result.error.message || result.error;
        if (errorMsg.includes('403') || errorMsg.includes('permission') || errorMsg.includes('row-level security')) {
          throw new Error('You do not have permission to create folders. Please contact your DealRoom administrator to update your role.');
        }
        throw new Error(errorMsg);
      }
      
      onMessage && onMessage('Folder created successfully', 'success');
      closeLocalModal('createFolder');
      await loadItems();
    } catch (error) {
      console.error('Folder creation error:', error);
      const errorMessage = error.message || String(error);
      onMessage && onMessage(`Failed to create folder: ${errorMessage}`, 'error');
    }
  }, [projectId, currentFolder, onMessage, closeLocalModal, loadItems]);

  const handleDeleteItem = useCallback(async (item) => {
    try {
      if (item.is_folder) {
        await supabase
          .from('dataroom_items')
          .delete()
          .eq('id', item.id);
      } else {
        if (item.file_path) {
          await supabase.storage
            .from('dataroom-files')
            .remove([item.file_path]);
        }
        await supabase
          .from('dataroom_items')
          .delete()
          .eq('id', item.id);
      }
      
      const action = item.is_embedded_folder ? 'Embed removed' : 'Item deleted';
      onMessage && onMessage(`${action} successfully`, 'success');
      setConfirmDelete(null);
      await loadItems();
    } catch (error) {
      onMessage && onMessage('Failed to delete item: ' + error.message, 'error');
    }
  }, [onMessage, loadItems]);

  const handleRenameItem = useCallback(async (item, newName) => {
    try {
      const { error } = await supabase
        .from('dataroom_items')
        .update({ name: newName })
        .eq('id', item.id);
        
      if (error) throw error;
      
      onMessage && onMessage('Item renamed successfully', 'success');
      setEditingItem(null);
      setEditName('');
      await loadItems();
    } catch (error) {
      onMessage && onMessage('Failed to rename item: ' + error.message, 'error');
    }
  }, [onMessage, loadItems]);

  // FIX: Removed handleTemplateSelect (unused code)

  // Handle move to root level
  // eslint-disable-next-line no-unused-vars
  const handleMoveToRoot = useCallback(async (itemId) => {
    try {
      const moveResult = await dataroomService.moveItemHierarchy(itemId, null, items);

      if (moveResult.success) {
        onMessage && onMessage('Item moved to root level successfully', 'success');
        await loadItems();
      } else {
        throw new Error(moveResult.error);
      }
    } catch (error) {
      onMessage && onMessage('Failed to move item to root: ' + error.message, 'error');
    }
  }, [onMessage, loadItems, items]);

  // Fixed drag and drop handler
  const handleDragEnd = useCallback(async (result) => {
    if (!result.destination) return;

    const draggedItemId = parseInt(result.draggableId);
    let destinationFolderId = null;
    const destId = result.destination.droppableId;
    
    // Handle different drop zones
    if (destId === 'dataroom-tree-root') {
      // Dropped on tree root - move to root level (parent_id = null)
      destinationFolderId = null;
    } else if (destId === 'dataroom-items') {
      // Dropped in main folder view - move to current folder (or root if currentFolder is null)
      destinationFolderId = currentFolder;
    } else if (destId.startsWith('folder-')) {
      // Dropped on a specific folder - move to that folder
      destinationFolderId = parseInt(destId.replace('folder-', ''));
    } else {
      // Unknown drop zone - cancel
      return;
    }
    
    // Don't move if source and destination are the same
    const draggedItem = items.find(item => item.id === draggedItemId);
    if (draggedItem && draggedItem.parent_id === destinationFolderId) {
      return; // Already in the correct location
    }
    
    try {
      const moveResult = await dataroomService.moveItemHierarchy(draggedItemId, destinationFolderId, items);

      if (moveResult.success) {
        onMessage && onMessage('Item moved successfully', 'success');
        await loadItems();
      } else {
        throw new Error(moveResult.error);
      }
    } catch (error) {
      onMessage && onMessage('Failed to move item: ' + error.message, 'error');
    }
  }, [currentFolder, onMessage, loadItems, items]);

  // Folder expansion toggle
  const handleToggleExpansion = useCallback((folderId) => {
    setExpandedFolders(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(folderId)) {
        newExpanded.delete(folderId);
      } else {
        newExpanded.add(folderId);
      }
      return newExpanded;
    });
  }, []);

  // Action menu handlers
  const handleStartRename = useCallback((item) => {
    setEditingItem(item);
    setEditName(item.name);
    setShowActionMenu(null);
  }, []);

  const handleCancelRename = useCallback(() => {
    setEditingItem(null);
    setEditName('');
  }, []);

  const handleConfirmRename = useCallback((item) => {
    if (editName.trim() && editName !== item.name) {
      handleRenameItem(item, editName.trim());
    } else {
      handleCancelRename();
    }
  }, [editName, handleRenameItem, handleCancelRename]);

  // Questions handling - COMPLETE IMPLEMENTATION with better context
  const handleCreateQuestionInternal = useCallback((contextData) => {
    let context;
    
    if (contextData.moduleReferenceId && contextData.moduleReferenceId !== projectId) {
      const item = items?.find(i => i.id === contextData.moduleReferenceId);
      const itemType = item?.is_folder ? 'Folder' : 'File';
      
      context = {
        moduleType: 'dataroom',
        contextId: contextData.moduleReferenceId,
        contextName: `${itemType}: ${contextData.moduleReferenceName || item?.name || 'Unknown Item'}`,
        moduleReferenceId: contextData.moduleReferenceId,
        moduleReferenceName: `${itemType}: ${contextData.moduleReferenceName || item?.name || 'Unknown Item'}`,
        projectId: projectId,
        category: item?.is_folder ? 'technical' : 'general'
      };
    } else {
      context = {
        moduleType: 'dataroom',
        contextId: projectId,
        contextName: `Dataroom: ${projectName || `Project ${projectId}`}`,
        moduleReferenceId: projectId,
        moduleReferenceName: `Dataroom: ${projectName || `Project ${projectId}`}`,
        projectId: projectId,
        category: 'general'
      };
    }
    
    onCreateQuestion(context); // Use the passed-in onCreateQuestion (which opens the modal in DataroomApp)
  }, [projectId, projectName, items, onCreateQuestion]);

  // Download index handler
  const handleDownloadIndex = () => {
    const downloadableContent = generateDownloadableIndex(items);
    const blob = new Blob([downloadableContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName || 'Dataroom'}_index.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Click outside handler for action menu
  useEffect(() => {
    const handleClickOutside = () => setShowActionMenu(null);
    if (showActionMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showActionMenu]);

  return (
    <>
      {/* Red Error Popup */}
      {showErrorPopup && (
        <div className="fixed top-4 right-4 z-[60] animate-slide-in">
          <div className="bg-red-600 text-white rounded-lg shadow-2xl p-6 max-w-md border-2 border-red-700">
            <div className="flex items-start">
              <AlertCircle className="h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-bold text-lg mb-2">File Not Allowed</h4>
                <p className="text-sm leading-relaxed whitespace-pre-line">{errorPopupMessage}</p>
              </div>
              <button
                onClick={() => setShowErrorPopup(false)}
                className="ml-4 text-white hover:text-red-200 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
      
      <div className="h-full flex flex-col">
        {/* Toolbar with Breadcrumbs */}
        <DataroomToolbar
        breadcrumb={breadcrumb}
        searchTerm={searchTerm}
        userRole={userRole}
        projectId={projectId}
        projectName={projectName}
        onNavigateToFolder={navigateToFolder}
        onSetSearchTerm={onSetSearchTerm}
        onOpenModal={handleOpenModal}
        onCreateQuestion={onCreateQuestion}
        onDownloadIndex={handleDownloadIndex}
      />
      
      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="h-full grid grid-cols-12 gap-4">
          {/* Tree View / Sidebar */}
          <div className="col-span-4 bg-white rounded-lg border border-gray-200 p-3 overflow-auto">
            <Droppable droppableId="dataroom-tree-root">
              {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1">
                      {treeData.map((item) => (
                          <DataroomItemRenderer
                              key={item.id}
                              item={item}
                              level={0}
                              viewMode={'tree'}
                              index={item.index}
                              indexMap={indexMap}
                              isExpanded={expandedFolders.has(item.id)}
                              hasChildren={item.children && item.children.length > 0}
                              isEditing={editingItem?.id === item.id}
                              editName={editName}
                              showActionMenu={showActionMenu}
                              userRole={userRole}
                              expandedFolders={expandedFolders}
                              onToggleExpansion={handleToggleExpansion}
                              onNavigateToFolder={navigateToFolder}
                              onOpenModal={openLocalModal}
                              onSetEditName={onSetEditName}
                              onConfirmRename={handleConfirmRename}
                              onCancelRename={handleCancelRename}
                              onSetShowActionMenu={setShowActionMenu}
                              onStartRename={handleStartRename}
                              onSetConfirmDelete={setConfirmDelete}
                              onCreateQuestion={handleCreateQuestionInternal}
                              formatFileSize={formatFileSize}
                              selectedFolderId={selectedFolderId}
                          />
                      ))}
                      {provided.placeholder}
                  </div>
              )}
            </Droppable>
          </div>
          
          {/* Main Folder View */}
          <div 
            className={`col-span-8 flex flex-col overflow-hidden ${dragActive ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
            onDragEnter={handleFileDrag}
            onDragOver={handleFileDrag}
            onDragLeave={handleFileDrag}
            onDrop={handleFileDrop}
          >
            {/* Folder Name Header */}
            {currentFolder && (
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  {items.find(i => i.id === currentFolder)?.name || 'Folder'}
                </h2>
              </div>
            )}
            
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Folder className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm ? 'No items found' : 'No files or folder.'}
                </h3>
                {!searchTerm && (
                  <div className="space-x-2">
                    <button
                      onClick={() => handleCreateQuestionInternal({
                        moduleType: 'dataroom',
                        contextId: currentFolder || projectId,
                        contextName: currentFolder 
                          ? (items.find(i => i.id === currentFolder)?.name || 'Folder')
                          : projectName,
                        moduleReferenceId: currentFolder || projectId,
                        moduleReferenceName: currentFolder 
                          ? (items.find(i => i.id === currentFolder)?.name || 'Folder')
                          : projectName
                      })}
                      className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Ask Question
                    </button>
                    <button
                      onClick={() => openLocalModal('bookmark', { 
                        itemId: currentFolder || projectId, 
                        itemName: currentFolder 
                          ? (items.find(i => i.id === currentFolder)?.name || 'Folder')
                          : `${projectName || 'Dataroom'} Home` 
                      })}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Bookmark className="h-4 w-4 mr-2" />
                      Bookmark Folder
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Main Droppable zone for the current folder view
              <Droppable droppableId="dataroom-items"> 
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={"space-y-2"}
                  >
                    {filteredItems.map((item, index) => (
                      <DataroomItemRenderer
                        key={item.id}
                        item={item}
                        level={0}
                        viewMode={'list'}
                        index={index}
                        indexMap={indexMap}
                        isExpanded={expandedFolders.has(item.id)}
                        hasChildren={false}
                        isEditing={editingItem?.id === item.id}
                        editName={editName}
                        showActionMenu={showActionMenu}
                        userRole={userRole}
                        expandedFolders={expandedFolders}
                        onToggleExpansion={handleToggleExpansion}
                        onNavigateToFolder={navigateToFolder}
                        onOpenModal={openLocalModal}
                        onSetEditName={onSetEditName}
                        onConfirmRename={handleConfirmRename}
                        onCancelRename={handleCancelRename}
                        onSetShowActionMenu={setShowActionMenu}
                        onStartRename={handleStartRename}
                        onSetConfirmDelete={setConfirmDelete}
                        onCreateQuestion={handleCreateQuestionInternal}
                        formatFileSize={formatFileSize}
                      />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
            </div>
          </div>
          </div>
        </DragDropContext>
      </div>

      {/* All Modals (Controlled by local state) */}
      
      {/* Upload Modal */}
      {localModals.upload && (
        <UploadFileModal
          isOpen={localModals.upload}
          onClose={() => closeLocalModal('upload')}
          projectId={projectId}
          currentFolder={currentFolder}
          onUploadComplete={handleFileUploadComplete}
        />
      )}

      {/* Create Folder Modal */}
      {localModals.createFolder && (
        <CreateFolderModal
          isOpen={localModals.createFolder}
          onClose={() => closeLocalModal('createFolder')}
          onCreateFolder={handleFolderCreated}
        />
      )}

      {/* Document Viewer */}
      {localModals.documentViewer && (
        <DocumentViewer
          isOpen={!!localModals.documentViewer}
          onClose={() => closeLocalModal('documentViewer')}
          item={localModals.documentViewer}
          projectId={projectId}
          user={user || {}}
        />
      )}

      {/* Permission Manager */}
      {localModals.permissionManager && (
        <PermissionManager
          isOpen={!!localModals.permissionManager}
          onClose={() => closeLocalModal('permissionManager')}
          itemId={localModals.permissionManager?.id} 
          projectId={projectId}
          itemName={localModals.permissionManager?.name}
          itemType={localModals.permissionManager?.type}
          user={user || {}}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!confirmDelete}
        item={confirmDelete}
        onConfirm={handleDeleteItem}
        onCancel={() => setConfirmDelete(null)}
      />
      
      {/* Project Color Modal */}
      {localModals.projectColor && (
        <ProjectColorModal
          isOpen={!!localModals.projectColor}
          onClose={() => closeLocalModal('projectColor')}
          project={localModals.projectColor}
          onColorChanged={(newColor) => {
            if (localModals.projectColor?.id) {
              onProjectColorChanged(localModals.projectColor.id, newColor);
            }
            closeLocalModal('projectColor');
          }}
        />
      )}
      
      {/* Bookmark Modal */}
      {localModals.bookmark && (
        <BookmarkModal
          isOpen={!!localModals.bookmark}
          onClose={() => closeLocalModal('bookmark')}
          user={user || {}}
          projectId={localModals.bookmark?.project_id || projectId}
          item={localModals.bookmark}
          onBookmarkAdded={handleBookmarkAdded}
          onMessage={onMessage}
        />
      )}
      
      {/* Modals passed from DataroomApp.js - controlled by parent state */}
      {modals.embed && (
        <EmbedDataroomModal
          isOpen={modals.embed}
          onClose={() => closeModal('embed')}
          currentProjectId={projectId}
          onSuccess={(result) => {
            onMessage && onMessage('Dataroom embedded successfully', 'success');
            closeModal('embed');
            loadItems();
          }}
          onMessage={onMessage}
        />
      )}

      {modals.merge && (
        <MergeDataroomModal
          isOpen={modals.merge}
          onClose={() => closeModal('merge')}
          currentProjectId={projectId}
          onSuccess={(result) => {
            onMessage && onMessage(`Merged ${result.itemsCopied} items successfully`, 'success');
            closeModal('merge');
            loadItems();
          }}
          onMessage={onMessage}
        />
      )}
    </div>
    </>
  );
};

export default React.memo(DataroomExplorer);